import { CommonModule, formatDate } from "@angular/common"
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Injector, OnInit, ViewChild, forwardRef, signal } from "@angular/core"
import { NG_VALUE_ACCESSOR, NgControl } from "@angular/forms"
import { CorpControlTitleComponent } from "../common/corp-control-title/corp-control-title.component"
import { CorpControlCommonComponent } from "../common/corp-control-common/corp-control-common.component"
import { TranslateModule } from '@ngx-translate/core'
import { lang } from 'libs/ui/component/src/lib/corp-date-picker/function/lang'

interface WeekItem {
    weekNumber: number
    startDate: Date
    endDate: Date
    isCurrentWeek: boolean
    isSelected: boolean
    year: number
}

interface DateRange {
    start: Date | null
    end: Date | null
}

@Component({
    selector: 'corp-date-picker',
    standalone: true,
    templateUrl: 'corp-date-picker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, CorpControlTitleComponent, TranslateModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CorpDatePickerComponent),
            multi: true,
        },
    ],
})
export class CorpDatePickerComponent extends CorpControlCommonComponent implements OnInit {
    @ViewChild('pickerContainer', { read: ElementRef }) pickerContainer?: ElementRef
    readonly lang = lang

    showCustomPicker = signal(false)
    currentMonth = signal(new Date())
    selectedDate = signal<Date | null>(null)
    calendarDays = signal<Array<{ date: Date; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean; isInRange?: boolean; isRangeStart?: boolean; isRangeEnd?: boolean; isHovered?: boolean }>>([])

    // Pour le date_range
    dateRange = signal<DateRange>({ start: null, end: null })
    isSelectingRange = signal(false)
    hoveredDate = signal<Date | null>(null)

    // Pour la vue des semaines
    weekList = signal<WeekItem[]>([])
    currentYear = signal(new Date().getFullYear())

    // Pour la vue des mois
    readonly monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

    // Pour la vue de l'heure
    hours: number[] = Array.from({ length: 24 }, (_, i) => i)
    minutes: number[] = Array.from({ length: 12 }, (_, i) => i * 5)
    selectedHour = signal<number>(new Date().getHours())
    selectedMinute = signal<number>(Math.floor(new Date().getMinutes() / 5) * 5)

    constructor(
        private injector: Injector,
        protected override cdr: ChangeDetectorRef,
        private elementRef: ElementRef
    ) {
        super()
    }

    ngOnInit(): void {
        const ngControl = this.injector.get(NgControl, null)
        this.setNgControl(ngControl)
        this.corpType = this.corpType == null ? 'date' : this.corpType

        if (this.corpType === 'date' || this.corpType === 'datetime-local' || this.corpType === 'date_range') {
            this.generateCalendar()
        } else if (this.corpType === 'week') {
            this.generateWeekList()
        }
    }

    togglePicker(): void {
        if (this.disabled || this.corpReadonly) return
        this.showCustomPicker.set(!this.showCustomPicker())
        if (this.showCustomPicker()) {
            if (this.corpType === 'date' || this.corpType === 'datetime-local' || this.corpType === 'date_range') {
                this.generateCalendar()
            } else if (this.corpType === 'week') {
                this.generateWeekList()
            }
        }
    }

    generateWeekList(): void {
        const year = this.currentYear()
        const weeks: WeekItem[] = []

        const firstDayOfYear = new Date(year, 0, 1)
        const firstMonday = new Date(firstDayOfYear)
        const dayOfWeek = firstDayOfYear.getDay()
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        firstMonday.setDate(firstDayOfYear.getDate() + daysToMonday)

        let currentDate = new Date(firstMonday)
        let weekNumber = 1

        while (weekNumber <= 52 && currentDate.getFullYear() <= year) {
            const startDate = new Date(currentDate)
            const endDate = new Date(currentDate)
            endDate.setDate(endDate.getDate() + 6)

            weeks.push({
                weekNumber,
                startDate,
                endDate,
                isCurrentWeek: this.isCurrentWeek(startDate),
                isSelected: this.isSelectedWeek(startDate),
                year
            })

            currentDate.setDate(currentDate.getDate() + 7)
            weekNumber++
        }

        this.weekList.set(weeks)
    }

    isCurrentWeek(date: Date): boolean {
        const today = new Date()
        const weekStart = this.getWeekStart(today)
        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekEnd.getDate() + 6)

        return date >= weekStart && date <= weekEnd
    }

    isSelectedWeek(date: Date): boolean {
        if (!this.selectedDate()) return false
        const selected = this.selectedDate()!
        const selectedWeekStart = this.getWeekStart(selected)
        const weekStart = this.getWeekStart(date)

        return selectedWeekStart.getTime() === weekStart.getTime()
    }

    getWeekStart(date: Date): Date {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() - day + (day === 0 ? -6 : 1)
        return new Date(d.setDate(diff))
    }

    selectWeek(week: WeekItem): void {
        this.selectedDate.set(week.startDate)
        this.corpValue = this.getFormatDate(week.startDate)
        this.value = this.corpValue
        this.onChange(this.value)
        this.showCustomPicker.set(false)
        this.cdr.markForCheck()
    }

    selectThisWeek(): void {
        const today = new Date()
        const weekStart = this.getWeekStart(today)
        const weekNumber = this.getISOWeekNumber(weekStart)

        const week: WeekItem = {
            weekNumber,
            startDate: weekStart,
            endDate: new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000),
            isCurrentWeek: true,
            isSelected: false,
            year: weekStart.getFullYear()
        }

        this.selectWeek(week)
    }

    getISOWeekNumber(date: Date): number {
        const d = new Date(date)
        d.setHours(0, 0, 0, 0)
        d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7)
        const week1 = new Date(d.getFullYear(), 0, 4)
        return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7)
    }

    generateCalendar(): void {
        const year = this.currentMonth().getFullYear()
        const month = this.currentMonth().getMonth()
        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const prevMonthLastDay = new Date(year, month, 0)

        const firstDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
        const days: Array<{ date: Date; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean; isInRange?: boolean; isRangeStart?: boolean; isRangeEnd?: boolean; isHovered?: boolean }> = []

        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            const date = new Date(year, month - 1, prevMonthLastDay.getDate() - i)
            days.push({
                date,
                isCurrentMonth: false,
                isToday: this.isToday(date),
                isSelected: this.isSelectedDate(date),
                isInRange: this.isDateInRange(date),
                isRangeStart: this.isRangeStart(date),
                isRangeEnd: this.isRangeEnd(date),
                isHovered: this.isDateHovered(date)
            })
        }

        for (let day = 1; day <= lastDay.getDate(); day++) {
            const date = new Date(year, month, day)
            days.push({
                date,
                isCurrentMonth: true,
                isToday: this.isToday(date),
                isSelected: this.isSelectedDate(date),
                isInRange: this.isDateInRange(date),
                isRangeStart: this.isRangeStart(date),
                isRangeEnd: this.isRangeEnd(date),
                isHovered: this.isDateHovered(date)
            })
        }

        const remainingDays = 42 - days.length
        for (let day = 1; day <= remainingDays; day++) {
            const date = new Date(year, month + 1, day)
            days.push({
                date,
                isCurrentMonth: false,
                isToday: this.isToday(date),
                isSelected: this.isSelectedDate(date),
                isInRange: this.isDateInRange(date),
                isRangeStart: this.isRangeStart(date),
                isRangeEnd: this.isRangeEnd(date),
                isHovered: this.isDateHovered(date)
            })
        }

        this.calendarDays.set(days)
    }

    isToday(date: Date): boolean {
        const today = new Date()
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
    }

    isSelectedDate(date: Date): boolean {
        if (this.corpType === 'date_range') return false
        if (!this.selectedDate()) return false
        const selected = this.selectedDate()!
        return date.getDate() === selected.getDate() &&
            date.getMonth() === selected.getMonth() &&
            date.getFullYear() === selected.getFullYear()
    }

    // Méthodes pour le date_range
    isDateInRange(date: Date): boolean {
        if (this.corpType !== 'date_range') return false
        const range = this.dateRange()
        if (!range.start) return false

        const compareDate = new Date(date)
        compareDate.setHours(0, 0, 0, 0)

        if (range.end) {
            const start = new Date(range.start)
            start.setHours(0, 0, 0, 0)
            const end = new Date(range.end)
            end.setHours(0, 0, 0, 0)
            return compareDate >= start && compareDate <= end
        }

        // Mode hover pendant la sélection
        if (this.isSelectingRange() && this.hoveredDate()) {
            const start = new Date(range.start)
            start.setHours(0, 0, 0, 0)
            const hovered = new Date(this.hoveredDate()!)
            hovered.setHours(0, 0, 0, 0)

            const min = start < hovered ? start : hovered
            const max = start > hovered ? start : hovered
            return compareDate >= min && compareDate <= max
        }

        return false
    }

    isRangeStart(date: Date): boolean {
        if (this.corpType !== 'date_range') return false
        const range = this.dateRange()
        if (!range.start) return false

        const compareDate = new Date(date)
        compareDate.setHours(0, 0, 0, 0)
        const start = new Date(range.start)
        start.setHours(0, 0, 0, 0)

        return compareDate.getTime() === start.getTime()
    }

    isRangeEnd(date: Date): boolean {
        if (this.corpType !== 'date_range') return false
        const range = this.dateRange()
        if (!range.end) return false

        const compareDate = new Date(date)
        compareDate.setHours(0, 0, 0, 0)
        const end = new Date(range.end)
        end.setHours(0, 0, 0, 0)

        return compareDate.getTime() === end.getTime()
    }

    isDateHovered(date: Date): boolean {
        if (this.corpType !== 'date_range') return false
        if (!this.isSelectingRange() || !this.hoveredDate()) return false

        const compareDate = new Date(date)
        compareDate.setHours(0, 0, 0, 0)
        const hovered = new Date(this.hoveredDate()!)
        hovered.setHours(0, 0, 0, 0)

        return compareDate.getTime() === hovered.getTime()
    }

    onDateRangeMouseDown(day: { date: Date; isCurrentMonth: boolean }): void {
        if (!day.isCurrentMonth) {
            this.currentMonth.set(new Date(day.date.getFullYear(), day.date.getMonth(), 1))
            this.generateCalendar()
            return
        }

        const range = this.dateRange()

        // Si on a déjà une plage complète, on recommence
        if (range.start && range.end) {
            this.dateRange.set({ start: day.date, end: null })
            this.isSelectingRange.set(true)
        }
        // Si on n'a pas de début, on commence
        else if (!range.start) {
            this.dateRange.set({ start: day.date, end: null })
            this.isSelectingRange.set(true)
        }
        // Si on a un début, on définit la fin
        else {
            const start = range.start
            const end = day.date

            if (start > end) {
                this.dateRange.set({ start: end, end: start })
            } else {
                this.dateRange.set({ start, end })
            }

            this.isSelectingRange.set(false)
            this.applyDateRange()
        }

        this.generateCalendar()
        this.cdr.markForCheck()
    }

    onDateRangeMouseEnter(day: { date: Date; isCurrentMonth: boolean }): void {
        if (!day.isCurrentMonth || !this.isSelectingRange()) return
        this.hoveredDate.set(day.date)
        this.generateCalendar()
        this.cdr.markForCheck()
    }

    applyDateRange(): void {
        const range = this.dateRange()
        if (!range.start || !range.end) return

        this.corpValue = this.getFormatDate(range)
        this.value = this.corpValue
        this.onChange(this.value)
        this.showCustomPicker.set(false)
        this.cdr.markForCheck()
    }

    clearDateRange(): void {
        this.dateRange.set({ start: null, end: null })
        this.isSelectingRange.set(false)
        this.hoveredDate.set(null)
        this.corpValue = null
        this.value = null
        this.onChange(null)
        this.generateCalendar()
        this.cdr.markForCheck()
    }

    isCurrentMonth(monthIndex: number): boolean {
        const today = new Date()
        return monthIndex === today.getMonth() &&
            this.currentMonth().getFullYear() === today.getFullYear()
    }

    isSelectedMonth(monthIndex: number): boolean {
        if (!this.selectedDate()) return false
        const selected = this.selectedDate()!
        return monthIndex === selected.getMonth() &&
            this.currentMonth().getFullYear() === selected.getFullYear()
    }

    selectDate(day: { date: Date; isCurrentMonth: boolean }): void {
        if (!day.isCurrentMonth) {
            this.currentMonth.set(new Date(day.date.getFullYear(), day.date.getMonth(), 1))
            this.generateCalendar()
            return
        }

        this.selectedDate.set(day.date)
        this.corpValue = this.getFormatDate(day.date)
        this.value = this.corpValue
        this.onChange(this.value)
        this.showCustomPicker.set(false)
        this.cdr.markForCheck()
    }

    selectDateTimeDate(day: { date: Date; isCurrentMonth: boolean }): void {
        if (!day.isCurrentMonth) {
            this.currentMonth.set(new Date(day.date.getFullYear(), day.date.getMonth(), 1))
            this.generateCalendar()
            return
        }

        const newDate = new Date(day.date)
        newDate.setHours(this.selectedHour(), this.selectedMinute(), 0, 0)
        this.selectedDate.set(newDate)
        this.generateCalendar()
        this.cdr.markForCheck()
    }

    selectMonth(monthIndex: number): void {
        const year = this.currentMonth().getFullYear()
        const date = new Date(year, monthIndex, 1)
        this.selectedDate.set(date)
        this.corpValue = this.getFormatDate(date)
        this.value = this.corpValue
        this.onChange(this.value)
        this.showCustomPicker.set(false)
        this.cdr.markForCheck()
    }

    selectHour(hour: number): void {
        this.selectedHour.set(hour)
    }

    selectMinute(minute: number): void {
        this.selectedMinute.set(minute)
    }

    applyTime(): void {
        const date = new Date()
        date.setHours(this.selectedHour(), this.selectedMinute(), 0, 0)
        this.selectedDate.set(date)
        this.corpValue = this.getFormatDate(date)
        this.value = this.corpValue
        this.onChange(this.value)
        this.showCustomPicker.set(false)
        this.cdr.markForCheck()
    }

    applyDateTime(): void {
        if (!this.selectedDate()) {
            this.selectedDate.set(new Date())
        }
        const date = this.selectedDate()!
        date.setHours(this.selectedHour(), this.selectedMinute(), 0, 0)
        this.corpValue = this.getFormatDate(date)
        this.value = this.corpValue
        this.onChange(this.value)
        this.showCustomPicker.set(false)
        this.cdr.markForCheck()
    }

    selectToday(): void {
        const today = new Date()
        this.selectDate({ date: today, isCurrentMonth: true })
    }

    selectThisMonth(): void {
        const today = new Date()
        this.selectMonth(today.getMonth())
    }

    previousMonth(): void {
        const current = this.currentMonth()
        this.currentMonth.set(new Date(current.getFullYear(), current.getMonth() - 1, 1))
        this.generateCalendar()
    }

    nextMonth(): void {
        const current = this.currentMonth()
        this.currentMonth.set(new Date(current.getFullYear(), current.getMonth() + 1, 1))
        this.generateCalendar()
    }

    previousYear(): void {
        if (this.corpType === 'week') {
            this.currentYear.set(this.currentYear() - 1)
            this.generateWeekList()
        } else {
            const current = this.currentMonth()
            this.currentMonth.set(new Date(current.getFullYear() - 1, current.getMonth(), 1))
        }
    }

    nextYear(): void {
        if (this.corpType === 'week') {
            this.currentYear.set(this.currentYear() + 1)
            this.generateWeekList()
        } else {
            const current = this.currentMonth()
            this.currentMonth.set(new Date(current.getFullYear() + 1, current.getMonth(), 1))
        }
    }

    getMonthName(): string {
        return this.currentMonth().toLocaleDateString('fr-FR', { month: 'long' })
    }

    getYear(): string {
        return this.currentMonth().toLocaleDateString('fr-FR', { year: 'numeric' })
    }

    getPlaceholder(): string {
        switch (this.corpType) {
            case 'date': return lang('Sélectionner une date')
            case 'week': return lang('Sélectionner une semaine')
            case 'month': return lang('Sélectionner un mois')
            case 'time': return lang('Sélectionner une heure')
            case 'datetime-local': return lang('Sélectionner date et heure')
            case 'date_range': return lang('Sélectionner une période')
            default: return lang('Sélectionner...')
        }
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.showCustomPicker.set(false)
            this.cdr.markForCheck()
        }
    }

    override writeValue(value: any): void {
        if (value === this.value) return

        if (value) {
            if (this.corpType === 'date_range' && typeof value === 'object' && value.start && value.end) {
                this.dateRange.set({
                    start: new Date(value.start),
                    end: new Date(value.end)
                })
            } else if (this.corpType === 'time' && typeof value === 'string') {
                const parts = value.split(':')
                const date = new Date()
                date.setHours(parseInt(parts[0] || '0'), parseInt(parts[1] || '0'), 0, 0)
                this.selectedDate.set(date)
                this.selectedHour.set(date.getHours())
                this.selectedMinute.set(date.getMinutes())
            } else {
                const date = new Date(value)
                this.selectedDate.set(date)
                this.selectedHour.set(date.getHours())
                this.selectedMinute.set(date.getMinutes())
            }

            if (this.corpType === 'week') {
                this.currentYear.set(this.selectedDate()!.getFullYear())
            } else if (this.corpType !== 'date_range') {
                this.currentMonth.set(this.selectedDate()!)
            }
        } else {
            this.selectedDate.set(null)
            this.dateRange.set({ start: null, end: null })
        }

        this.corpValue = this.getFormatDate(value)
        this.value = this.corpValue
        this.cdr.markForCheck()
    }

    getFormatDate(dateValue: any): string | null {
        if (!dateValue) return null

        if (this.corpType === 'date_range') {
            const range = dateValue as DateRange
            if (!range.start || !range.end) return null
            const start = formatDate(range.start, 'yyyy-MM-dd', 'en')
            const end = formatDate(range.end, 'yyyy-MM-dd', 'en')
            return `${start} / ${end}`
        }

        const date = new Date(dateValue)

        if (this.corpType == 'date')
            return formatDate(date, 'yyyy-MM-dd', 'en')
        if (this.corpType == 'datetime-local')
            return formatDate(date, 'yyyy-MM-dd\'T\'HH:mm', 'en')
        if (this.corpType == 'month')
            return formatDate(date, 'yyyy-MM', 'en')
        if (this.corpType == 'time')
            return formatDate(date, 'HH:mm:ss', 'en')
        if (this.corpType == 'week') {
            const weekNumber = this.getISOWeekNumber(date)
            return `${date.getFullYear()}-W${weekNumber.toString().padStart(2, "0")}`
        }
        return null
    }

    getDisplayDate(): string {
        if (!this.corpValue) return ''

        if (this.corpType === 'date_range') {
            const range = this.dateRange()
            if (!range.start || !range.end) return ''
            const start = range.start.toLocaleDateString('fr-FR')
            const end = range.end.toLocaleDateString('fr-FR')
            return `${start} - ${end}`
        }

        if (!this.selectedDate()) return String(this.corpValue ?? '')

        const date = this.selectedDate()!

        if (this.corpType === 'date')
            return date.toLocaleDateString('fr-FR')
        if (this.corpType === 'month')
            return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        if (this.corpType === 'week') {
            const weekNumber = this.getISOWeekNumber(date)
            return `Semaine ${weekNumber}, ${date.getFullYear()}`
        }
        if (this.corpType === 'time') {
            const h = date.getHours().toString().padStart(2, '0')
            const m = date.getMinutes().toString().padStart(2, '0')
            return `${h}:${m}`
        }
        if (this.corpType === 'datetime-local') {
            return date.toLocaleDateString('fr-FR') + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        }

        return String(this.corpValue ?? '')
    }
}
